/**
 * QueuedRuleGroup — one routing-rule sub-section inside a layer's queued list:
 * a rule header + its own drag-to-reorder list. Dragging is scoped WITHIN the
 * rule (a queued path can only be promoted into its own rule's slot); the parent
 * rebuilds the full layer order so bank priorities stay dense.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ChevronRight } from 'lucide-react';
import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { QueueElement } from '@trafficpulse/shared';

import type { QueueProvider } from '@/features/trackers/flow-optimizer/queue-provider';
import {
  dragEndToMove,
  useBinomDragSensors,
} from '../../../standard/binom/workspace/list/use-binom-drag-sensors';
import { poolElementKey } from '../../rotation/rotation-swaps';
import { RuleBadge } from '../queue-cells';
import { type QueueRuleSection } from '../queue-rule-groups';
import type { BulkAwareProps } from '../../bulk/row/bulk-row.types';
import QueuedSortableRow from './QueuedSortableRow';

export interface QueuedRuleGroupProps extends BulkAwareProps {
  section: QueueRuleSection;
  /** Global 0-based rank of this section's first row within the layer. */
  rankOffset: number;
  incoming: Map<string, string>;
  campaignId: string;
  provider: QueueProvider;
  pending: boolean;
  /** Called with this section's new element order after a drop. */
  onReorder: (elements: QueueElement[]) => void;
  /** Folded sections keep their header so the rule and count stay readable. */
  open?: boolean;
  onToggleOpen?: () => void;
}

const QueuedRuleGroup: FC<QueuedRuleGroupProps> = ({
  section,
  rankOffset,
  incoming,
  campaignId,
  provider,
  pending,
  bulk,
  onReorder,
  open = true,
  onToggleOpen,
}) => {
  const sensors = useBinomDragSensors();
  const ids = section.elements.map((e) => e.bankId);
  const onDragEnd = (event: DragEndEvent) => {
    const move = dragEndToMove(event, ids);
    if (move) onReorder(arrayMove(section.elements, move.from, move.to));
  };
  return (
    <div className="rounded-md border border-border/50 bg-background/40 p-1.5">
      <p className="mb-1.5 flex items-center gap-1.5 px-1">
        {onToggleOpen ? (
          <button
            type="button"
            onClick={onToggleOpen}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${section.rule?.name ?? 'ungrouped'}`}
            className="-ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight
              className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`}
              aria-hidden
            />
          </button>
        ) : null}
        <RuleBadge rule={section.rule ?? undefined} />
        <span className="text-[11px] tabular-nums text-muted-foreground/70">
          · {section.elements.length}
        </span>
      </p>
      {open ? (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div className="space-y-1">
            {section.elements.map((el, i) => (
              <QueuedSortableRow
                key={el.bankId}
                element={el}
                rank={rankOffset + i + 1}
                isNext={rankOffset === 0 && i === 0}
                replaces={incoming.get(poolElementKey(el))}
                campaignId={campaignId}
                provider={provider}
                pending={pending}
                bulk={bulk}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      ) : null}
    </div>
  );
};

export default QueuedRuleGroup;
